import { useEffect } from 'react';
import { setCanonical, setMeta } from '../utils/seo';
import { SponsorBanner, AdSensePlaceholder, ClickBankBanner } from './AdSlots';
import { Link } from 'react-router-dom';

const BODY_TYPES = [
  { name: 'Rectangle', desc: 'Shoulders, waist, and hips are similar width.', tips: 'Create curves with belted tops, A-line skirts, structured blazers. Peplum tops add definition.', menTips: 'Layering adds dimension. Structured shoulders in blazers work well. Avoid boxy fits.' },
  { name: 'Triangle (Pear)', desc: 'Hips wider than shoulders. Weight carried in lower body.', tips: 'Draw attention upward with detailed necklines, statement tops, dark bottoms. A-line and boot-cut pants balance proportions.', menTips: 'Slim or straight-leg pants. Structured shoulder jackets balance upper and lower body.' },
  { name: 'Inverted Triangle', desc: 'Shoulders wider than hips. Athletic build.', tips: 'Balance with fuller skirts, wide-leg pants, hip details. V-necks elongate torso. Avoid shoulder pads.', menTips: 'Slim-fit pants add proportion. Avoid oversized tops. V-necks work well.' },
  { name: 'Hourglass', desc: 'Balanced shoulders and hips with defined waist.', tips: 'Highlight waist with wrap dresses, belted styles, fitted tops. Avoid shapeless or boxy clothing.', menTips: 'Fitted clothes showcase proportions. Slim-fit shirts and tapered pants. Avoid baggy styles.' },
  { name: 'Apple (Round)', desc: 'Weight carried around the midsection. Fuller torso.', tips: 'Empire waist tops and dresses elongate. V-necks and vertical patterns slim. Dark, solid colors for bottoms.', menTips: 'Structured jackets with slight taper. Straight-leg pants. Vertical stripes and darker midtones.' },
  { name: 'Athletic', desc: 'Broad shoulders, defined muscles, narrow hips.', tips: 'Soften with flowing fabrics, curved hemlines. Off-shoulder tops and A-line skirts add femininity.', menTips: 'Most fits work well. Slim-fit highlights build. Avoid compression fit for casual wear.' },
];

export default function BodyTypeGuide() {
  useEffect(() => {
    setCanonical('/body-type-guide');
    setMeta('Body Type Style Guide', 'Discover which clothing styles flatter your body type. Expert tips for rectangle, pear, hourglass, apple, and athletic builds.');
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12">
      <div className="text-center mb-12">
        <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Style Guide</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1C1917] mb-4">Dress for Your Body Type</h1>
        <p className="text-[#78716C] max-w-xl mx-auto">Understanding your body shape is the first step to finding clothes that fit and flatter. Here's what works best for each type.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {BODY_TYPES.map(bt => (
          <div key={bt.name} className="bg-white border border-[#E8E2D9] p-6 hover:shadow-md transition-shadow" data-testid={`body-type-${bt.name.toLowerCase().replace(/[\s()]/g, '-')}`}>
            <h3 className="font-['Cormorant_Garamond'] text-2xl font-semibold text-[#1C1917] mb-2">{bt.name}</h3>
            <p className="text-sm text-[#78716C] mb-4">{bt.desc}</p>
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A028] mb-1">Women's Tips</p>
                <p className="text-sm text-[#78716C] leading-relaxed">{bt.tips}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-[#C5A028] mb-1">Men's Tips</p>
                <p className="text-sm text-[#78716C] leading-relaxed">{bt.menTips}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="text-center mt-10">
        <p className="text-sm text-[#78716C] mb-4">Know your body type but need exact sizes?</p>
        <Link to="/find-my-size" className="inline-flex items-center gap-2 bg-[#1C1917] text-white px-8 py-3 text-sm font-medium hover:bg-[#C5A028] transition-colors" data-testid="bodytype-to-quiz">Find My Size Now</Link>
      </div>
      <SponsorBanner />
      <ClickBankBanner />
      <AdSensePlaceholder />
    </div>
  );
}
